// Shared definitions for per-category product attribute schemas (Task #117).
//
// Each category can have an `attributesSchema` JSON column on the
// `categories` row that drives both the admin Product Attributes editor
// and the storefront attribute chips on product cards / PDP.  When that
// column is NULL we fall back to the built-in defaults below so behavior
// is unchanged for rows that haven't been migrated yet.

export type AttributeFieldType = "text" | "number" | "boolean" | "select";

export interface AttributeField {
  key: string;
  label: string;
  type: AttributeFieldType;
  options?: string[];
  unit?: string; // optional display suffix (e.g. "BTU", "in", "Hz")
}

export const DEFAULT_CATEGORY_ATTRIBUTES: Record<string, AttributeField[]> = {
  Refrigerators: [
    {
      key: "door_style",
      label: "Door Style",
      type: "select",
      options: [
        "Single Door",
        "French Door",
        "Side by Side",
        "Top Freezer",
        "Bottom Freezer",
      ],
    },
    { key: "ice_maker", label: "Ice Maker", type: "boolean" },
    { key: "water_dispenser", label: "Water Dispenser", type: "boolean" },
  ],
  "Air Conditioners": [
    { key: "btu", label: "BTU Rating", type: "number", unit: "BTU" },
    {
      key: "coverage_area_sqft",
      label: "Coverage Area",
      type: "number",
      unit: "sq ft",
    },
    {
      key: "ac_type",
      label: "Type",
      type: "select",
      options: ["Window", "Portable", "Mini-Split", "Through-the-Wall"],
    },
    { key: "energy_star", label: "Energy Star Certified", type: "boolean" },
    {
      key: "voltage",
      label: "Voltage",
      type: "select",
      options: ["115V", "230V"],
    },
    {
      key: "plug_type",
      label: "Plug Type",
      type: "select",
      options: [
        "Standard 115V",
        "NEMA 6-15",
        "NEMA 6-20",
        "NEMA 6-30",
        "NEMA 14-30",
      ],
    },
    { key: "with_heat", label: "Heat Mode", type: "boolean" },
    { key: "smart_wifi", label: "Smart / Wi-Fi", type: "boolean" },
  ],
  "Window Air Conditioners": [
    { key: "btu", label: "BTU Rating", type: "number", unit: "BTU" },
    {
      key: "coverage_area_sqft",
      label: "Coverage Area",
      type: "number",
      unit: "sq ft",
    },
    { key: "energy_star", label: "Energy Star Certified", type: "boolean" },
  ],
  Dehumidifiers: [
    {
      key: "capacity_pints",
      label: "Capacity",
      type: "number",
      unit: "pints/day",
    },
    {
      key: "coverage_area_sqft",
      label: "Coverage Area",
      type: "number",
      unit: "sq ft",
    },
    { key: "energy_star", label: "Energy Star Certified", type: "boolean" },
  ],
  TVs: [
    {
      key: "screen_size",
      label: "Screen Size",
      type: "number",
      unit: '"',
    },
    {
      key: "resolution",
      label: "Resolution",
      type: "select",
      options: ["720p", "1080p", "4K", "8K"],
    },
    { key: "smart_tv", label: "Smart TV", type: "boolean" },
    {
      key: "refresh_rate",
      label: "Refresh Rate",
      type: "number",
      unit: "Hz",
    },
  ],
  "Small Appliances": [
    { key: "wattage", label: "Wattage", type: "number", unit: "W" },
    { key: "cordless", label: "Cordless", type: "boolean" },
  ],
};

/**
 * Resolve the attribute schema for a category. Prefers the persisted
 * `attributesSchema` JSON if valid; otherwise falls back to defaults
 * keyed by the category name (case-insensitive). Returns [] when no
 * defaults exist.
 */
export function resolveCategoryAttributeSchema(
  categoryName: string | null | undefined,
  persisted: unknown,
): AttributeField[] {
  if (Array.isArray(persisted)) {
    const cleaned = persisted
      .filter((f: any) => f && typeof f.key === "string" && typeof f.label === "string")
      .map((f: any) => ({
        key: String(f.key),
        label: String(f.label),
        type: (["text", "number", "boolean", "select"].includes(f.type)
          ? f.type
          : "text") as AttributeFieldType,
        options: Array.isArray(f.options)
          ? f.options.map((o: any) => String(o))
          : undefined,
        unit: typeof f.unit === "string" ? f.unit : undefined,
      }));
    if (cleaned.length > 0) return cleaned;
  }
  // Defensive: callers occasionally pass the entire category object
  // (`{id, slug, name}`) instead of the bare name string. Coerce so a
  // bad input degrades to "no schema" rather than crashing the gallery.
  if (categoryName && typeof categoryName !== "string") {
    categoryName = (categoryName as any)?.name;
  }
  if (!categoryName || typeof categoryName !== "string") return [];
  // Case-insensitive lookup against defaults
  const exact = DEFAULT_CATEGORY_ATTRIBUTES[categoryName];
  if (exact) return exact;
  const lower = categoryName.toLowerCase();
  for (const [k, v] of Object.entries(DEFAULT_CATEGORY_ATTRIBUTES)) {
    if (k.toLowerCase() === lower) return v;
  }
  return [];
}

/**
 * Format an attribute value for chip display on product cards / PDP.
 * Returns null when the value should be hidden (empty, false, etc.).
 */
export function formatAttributeChip(
  field: AttributeField,
  value: unknown,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (field.type === "boolean") {
    return value === true ? field.label : null;
  }
  if (field.type === "number") {
    const num = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(num)) return null;
    return field.unit ? `${num} ${field.unit}`.replace(/ "$/, '"') : String(num);
  }
  if (field.type === "select") {
    return String(value);
  }
  // text
  const s = String(value).trim();
  if (!s) return null;
  return s;
}
